import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LocaleTypes,
  SDKEnvironmentTypes,
  SelfieCameraTypes,
  UnicoCheckBuilder,
  UnicoConfig,
  UnicoThemeBuilder,
} from '../utils/unico-sdk.utils';
import { useScript } from './useScript';
import { UnicoContext, CaptureStatus, UnicoCaptureResult, UnicoContextValue } from './unico-context';
import { buildProxyReadyPayload, dispatchProxyPayload } from '../services/proxy-dispatcher';
import logger from '../utils/logger.utils';
import { getUnicoErrorDetailsForLog, getUnicoErrorSearchText } from '../utils/unico-error.utils';
import { scheduleUnicoSdkDomCleanup } from '../utils/unico-dom-cleanup.utils';
import { performAggressiveUnicoCleanupAsync } from '../utils/unico-aggressive-cleanup.utils';

const UNICO_SCRIPT_ID = 'unico-webframe-sdk';
const PROCESS_ID_STORAGE_KEY = 'unico_process_id';
const TOKEN_TTL_SECONDS = 60;

type SdkErrorShape = { code?: string | number; message?: string };
type CameraOpenerShape = {
  open: (options: {
    on: {
      success: (p: { encrypted: string; base64: string }) => void;
      error: (e: unknown) => void;
      user_canceled: () => void;
    };
  }) => void;
  close?: () => void;
  destroy?: () => void;
};

const mapErrorMessage = (error: unknown) => {
  const joined = getUnicoErrorSearchText(error);

  const explicitUserCancelSignals = [
    'user_canceled',
    'user canceled',
    'cancelled by user',
    'canceled by user',
    'cancel button',
    'pressed the cancel button',
    'did not complete liveness',
    'did not complete session',
    'did not complete the session',
  ];

  // User pressed cancel button or closed the session - not an error
  if (explicitUserCancelSignals.some((signal) => joined.includes(signal)))
    return null;

  // Permission: only explicit browser permission errors
  if (joined.includes('notallowederror') || (joined.includes('permission') && joined.includes('denied')))
    return 'Permissão da câmera negada. Libere o acesso e tente novamente.';

  const sessionInterruptedSignals = [
    'session interrupted',
    'session was interrupted',
    'session was cancelled',
    'session canceled',
    'session cancelled',
    'app went to background',
    'app going to background',
    'application backgrounded',
    'due to the app',
    'visibilitystate hidden',
    'document hidden',
    'pagehide',
    'background',
  ];

  // Session interrupted (switched tab, OS notification, app in background)
  if (sessionInterruptedSignals.some((signal) => joined.includes(signal)))
    return 'Sessão interrompida. Volte para o app e tente novamente.';

  // SDK internal failures
  if (
    joined.includes('faceworker') ||
    joined.includes('dorothy') ||
    joined.includes('wasm') ||
    joined.includes('webgl') ||
    joined.includes('shader')
  )
    return 'Falha interna do SDK de biometria. Atualize o navegador ou tente outro dispositivo.';

  if (joined.includes('iframe'))
    return 'A câmera não pode ser aberta dentro de iframe neste ambiente.';

  // Missing SDK assets / script loading errors
  if (
    joined.includes('err_aborted') ||
    joined.includes('aborterror') ||
    joined.includes('404') ||
    joined.includes('not found') ||
    joined.includes('ozliveness_core')
  )
    return 'Falha ao carregar componentes da biometria. Tente novamente em instantes.';

  // Network / service errors
  if (
    joined.includes('network') ||
    joined.includes('503') ||
    joined.includes('502') ||
    joined.includes('failed to fetch') ||
    joined.includes('unavailable')
  )
    return 'Serviço de biometria temporariamente indisponível. Tente novamente em instantes.';

  const e = (error || {}) as SdkErrorShape;
  return e.message || 'Erro inesperado na biometria. Tente novamente.';
};

const isPermissionError = (error: unknown) => {
  const joined = getUnicoErrorSearchText(error);
  return joined.includes('notallowederror') || (joined.includes('permission') && joined.includes('denied'));
};


const buildTheme = () => {
  const builder = new UnicoThemeBuilder() as any;
  if (typeof builder.setColorSilhouetteSuccess === 'function') builder.setColorSilhouetteSuccess('#0090ff');
  if (typeof builder.setColorSilhouetteError === 'function') builder.setColorSilhouetteError('#d50000');
  if (typeof builder.setColorSilhouetteNeutral === 'function') builder.setColorSilhouetteNeutral('#fcfcfc');
  if (typeof builder.setBackgroundColor === 'function') builder.setBackgroundColor('#111111');
  if (typeof builder.setColorText === 'function') builder.setColorText('#ffffff');
  if (typeof builder.setBackgroundColorComponents === 'function') builder.setBackgroundColorComponents('#0090ff');
  if (typeof builder.setColorTextComponents === 'function') builder.setColorTextComponents('#ffffff');
  if (typeof builder.setBackgroundColorButtons === 'function') builder.setBackgroundColorButtons('#0090ff');
  if (typeof builder.setColorTextButtons === 'function') builder.setColorTextButtons('#ffffff');
  if (typeof builder.setBackgroundColorBoxMessage === 'function') builder.setBackgroundColorBoxMessage('#ffffff');
  if (typeof builder.setColorTextBoxMessage === 'function') builder.setColorTextBoxMessage('#111111');
  return builder.build();
};

const forcePtBrLocale = (builder: unknown) => {
  const safeBuilder = builder as {
    setLocale?: (value: unknown) => unknown;
    setLanguage?: (value: unknown) => unknown;
  };

  const localeValue = (LocaleTypes as { PT_BR?: unknown } | undefined)?.PT_BR ?? 'pt-br';

  logger.info('[Unico] forcePtBrLocale', {
    localeValue,
    hasSetLocale: typeof safeBuilder.setLocale === 'function',
    hasSetLanguage: typeof safeBuilder.setLanguage === 'function'
  });

  if (typeof safeBuilder.setLocale === 'function') {
    try {
      safeBuilder.setLocale(localeValue);
      logger.info('[Unico] setLocale chamado com sucesso', { localeValue });
      return;
    } catch (e) {
      logger.warn('[Unico] setLocale falhou', { localeValue, error: e });
    }
  }

  if (typeof safeBuilder.setLanguage === 'function') {
    try {
      safeBuilder.setLanguage(localeValue);
      logger.info('[Unico] setLanguage chamado com sucesso', { localeValue });
    } catch (e) {
      logger.warn('[Unico] setLanguage falhou', { localeValue, error: e });
    }
  }
};

const DESKTOP_FAKE_WIDTH = 1024;
const DESKTOP_FAKE_HEIGHT = 768;

const fakeViewportHandles: { restore?: () => void } = {};

const installFakeDesktopViewport = () => {
  if (fakeViewportHandles.restore) return;
  const origInnerWidth = Object.getOwnPropertyDescriptor(window, 'innerWidth');
  const origInnerHeight = Object.getOwnPropertyDescriptor(window, 'innerHeight');
  try {
    Object.defineProperty(window, 'innerWidth', { configurable: true, get: () => DESKTOP_FAKE_WIDTH });
    Object.defineProperty(window, 'innerHeight', { configurable: true, get: () => DESKTOP_FAKE_HEIGHT });
  } catch (e) {
    console.warn('[Unico] could not override innerWidth/innerHeight', e);
  }
  fakeViewportHandles.restore = () => {
    try {
      if (origInnerWidth) Object.defineProperty(window, 'innerWidth', origInnerWidth);
      else delete (window as any).innerWidth;
      if (origInnerHeight) Object.defineProperty(window, 'innerHeight', origInnerHeight);
      else delete (window as any).innerHeight;
    } catch { /* noop */ }
    fakeViewportHandles.restore = undefined;
  };
};

const restoreViewport = () => {
  fakeViewportHandles.restore?.();
};

const stopCameraTracksFromDom = () => {
  document.querySelectorAll('video').forEach((video) => {
    const stream = video.srcObject as MediaStream;
    if (!stream) return;
    stream.getTracks().forEach((t) => t.stop());
    video.srcObject = null;
  });
};

const normalizeHostname = (v: string) => v.trim().replace(/\/+$/, '');

export const UnicoProvider = ({ children }: { children: React.ReactNode }) => {

  const [status, setStatus] = useState<CaptureStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UnicoCaptureResult | null>(null);

  const callbackLockRef = useRef(false);
  const openerRef = useRef<CameraOpenerShape | null>(null);
  const mountedRef = useRef(false);
  const processIdRef = useRef<string | null>(null);
  const cameraInstanceRef = useRef<any>(null);
  const configCacheRef = useRef<{
    hostname: string;
    hostKey: string;
    modelsPath: string;
    appEnvironment: any;
  } | null>(null);

  const sdkScriptUrl = process.env.REACT_APP_UNICO_SDK_SCRIPT_URL ?? '';
  const scriptStatus = useScript(sdkScriptUrl, UNICO_SCRIPT_ID);
  const sdkReady = sdkScriptUrl ? scriptStatus === 'ready' : true;
  const sdkFailed = sdkScriptUrl ? scriptStatus === 'error' : false;

  const configuredEnvironment =
    (process.env.REACT_APP_UNICO_ENVIRONMENT || 'UAT').toUpperCase() === 'PROD' ? 'PROD' : 'UAT';
  const appEnvironment = configuredEnvironment === 'PROD' ? SDKEnvironmentTypes.PROD : SDKEnvironmentTypes.UAT;
  const hostByEnvironment =
    configuredEnvironment === 'PROD'
      ? process.env.REACT_APP_UNICO_HOSTNAME_PROD || process.env.REACT_APP_UNICO_HOSTNAME
      : process.env.REACT_APP_UNICO_HOSTNAME_QA || process.env.REACT_APP_UNICO_HOSTNAME;
  const keyByEnvironment =
    configuredEnvironment === 'PROD'
      ? process.env.REACT_APP_UNICO_HOST_KEY_PROD || process.env.REACT_APP_UNICO_HOST_KEY
      : process.env.REACT_APP_UNICO_HOST_KEY_QA || process.env.REACT_APP_UNICO_HOST_KEY;
  const modelsPath = process.env.REACT_APP_UNICO_MODELS_PATH || '/models';

  const safeSetStatus = useCallback((next: CaptureStatus) => {
    if (!mountedRef.current) return;
    setStatus(next);
  }, []);

  const safeSetError = useCallback((message: string | null) => {
    if (!mountedRef.current) return;
    setError(message);
  }, []);

  const safeSetResult = useCallback((next: UnicoCaptureResult | null) => {
    if (!mountedRef.current) return;
    setResult(next);
  }, []);

  const releaseCameraResources = useCallback(() => {
    if (openerRef.current?.close) openerRef.current.close();
    if (openerRef.current?.destroy) openerRef.current.destroy();
    openerRef.current = null;
    stopCameraTracksFromDom();
    scheduleUnicoSdkDomCleanup();
    restoreViewport();
  }, []);

  const getOrCreateProcessId = useCallback(() => {
    if (processIdRef.current) return processIdRef.current;
    const stored = sessionStorage.getItem(PROCESS_ID_STORAGE_KEY);
    const id = stored || crypto.randomUUID();
    processIdRef.current = id;
    sessionStorage.setItem(PROCESS_ID_STORAGE_KEY, id);
    return id;
  }, []);

  const clearProcessId = useCallback(() => {
    processIdRef.current = null;
    sessionStorage.removeItem(PROCESS_ID_STORAGE_KEY);
  }, []);

  const initializeSession = useCallback(() => {
    callbackLockRef.current = false;
    getOrCreateProcessId();
    safeSetError(null);
    safeSetResult(null);
    safeSetStatus(sdkReady ? 'idle' : 'sdk_loading');
  }, [getOrCreateProcessId, sdkReady, safeSetError, safeSetResult, safeSetStatus]);

  const cleanupSession = useCallback(() => {
    callbackLockRef.current = true;
    releaseCameraResources();
    safeSetStatus(sdkReady ? 'idle' : 'sdk_loading');
  }, [releaseCameraResources, sdkReady, safeSetStatus]);

  const resetCapture = useCallback(() => {
    callbackLockRef.current = false;
    releaseCameraResources();
    // Hard reset: drop the cached camera instance AND purge the #box-camera children so the
    // frozen FaceTec iframe/canvas/workers are evicted. Next openLivenessCapture rebuilds fresh.
    cameraInstanceRef.current = null;
    const boxCamera = document.getElementById('box-camera');
    if (boxCamera) {
      while (boxCamera.firstChild) boxCamera.removeChild(boxCamera.firstChild);
    }
    clearProcessId();
    getOrCreateProcessId();
    safeSetStatus(sdkReady ? 'idle' : 'sdk_loading');
    safeSetError(null);
    safeSetResult(null);
  }, [
    clearProcessId,
    getOrCreateProcessId,
    releaseCameraResources,
    sdkReady,
    safeSetError,
    safeSetResult,
    safeSetStatus,
  ]);

  const openLivenessCapture = useCallback(async () => {
    if (sdkFailed) {
      safeSetStatus('error');
      safeSetError('Falha ao carregar o SDK da Unico.');
      return;
    }
    if (!sdkReady) {
      safeSetStatus('sdk_loading');
      safeSetError(null);
      return;
    }
    if (!hostByEnvironment || !keyByEnvironment) {
      safeSetStatus('error');
      safeSetError('Credenciais da Unico nao configuradas. Verifique o .env.');
      return;
    }

    callbackLockRef.current = false;
    logger.info('[Unico] openLivenessCapture started', { process_id: getOrCreateProcessId() });

    // Purge stale SDK state from previous sessions (fire-and-forget — already ran on mount).
    // Do NOT await: IndexedDB ops on MIUI/Xiaomi are slow and block SDK init unnecessarily.
    performAggressiveUnicoCleanupAsync().catch(() => { /* non-fatal */ });

    releaseCameraResources();
    safeSetError(null);
    safeSetResult(null);
    safeSetStatus('awaiting_permission');

    try {

      // Cache config values to avoid dependency on env vars that change
      if (!configCacheRef.current ||
          configCacheRef.current.hostname !== hostByEnvironment ||
          configCacheRef.current.hostKey !== keyByEnvironment) {
        configCacheRef.current = {
          hostname: hostByEnvironment,
          hostKey: keyByEnvironment,
          modelsPath,
          appEnvironment,
        };
      }

      const cachedConfig = configCacheRef.current;
      logger.info('[Unico] building UnicoConfig', {
        process_id: getOrCreateProcessId(),
        hostname: normalizeHostname(cachedConfig.hostname),
        environment: cachedConfig.appEnvironment,
        modelsPath: cachedConfig.modelsPath,
      });
      const config = new UnicoConfig().setHostname(normalizeHostname(cachedConfig.hostname)).setHostKey(cachedConfig.hostKey);

      // Rebuild camera instance every time (POC pattern — caching breaks SDK internal state between attempts)
      logger.info('[Unico] creating UnicoCheckBuilder', { process_id: getOrCreateProcessId() });
      const builder = new UnicoCheckBuilder();
      builder.setModelsPath(cachedConfig.modelsPath);
      builder.setEnvironment(cachedConfig.appEnvironment);
      builder.setTheme(buildTheme());

      const camera = builder.build();

      if (!camera) {
        throw new Error('builder.build() returned null or undefined');
      }

      cameraInstanceRef.current = camera;

      logger.info('[Unico] camera instance built and cached', {
        process_id: getOrCreateProcessId(),
        hasPrepareSelfieCamera: typeof camera?.prepareSelfieCamera === 'function'
      });


      // Verify box-camera container exists
      const boxCamera = document.getElementById('box-camera');
      if (!boxCamera) {
        throw new Error('box-camera container not found in DOM - SDK initialization failed');
      }
      logger.info('[Unico] box-camera container found', { process_id: getOrCreateProcessId() });

      logger.info('[Unico] preparing selfie camera', { process_id: getOrCreateProcessId() });

      // Add timeout to detect if prepareSelfieCamera hangs
      const prepareCameraWithTimeout = new Promise<CameraOpenerShape>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('prepareSelfieCamera timeout after 30s - SDK may be stuck loading WASM or assets'));
        }, 30000);

        camera.prepareSelfieCamera(config, SelfieCameraTypes.LIVENESS)
          .then((opener) => {
            clearTimeout(timeoutId);
            logger.info('[Unico] prepareSelfieCamera completed', { process_id: getOrCreateProcessId() });
            resolve(opener);
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            logger.error('[Unico] prepareSelfieCamera error', {
              process_id: getOrCreateProcessId(),
              error: err?.message || String(err),
            });
            reject(err);
          });
      });

      const cameraOpener = await prepareCameraWithTimeout;
      openerRef.current = cameraOpener;
      safeSetStatus('camera_active');
      logger.info('[Unico] camera_opened', { process_id: getOrCreateProcessId() });

      logger.info('[Unico] calling cameraOpener.open()', { process_id: getOrCreateProcessId() });

      // Activity-reset watchdog. FaceTec runs inside a cross-origin iframe, so events from there
      // do NOT bubble to the parent #box-camera. We listen on `window` (which still receives
      // global signals like visibilitychange, blur, focus, key/pointer on the host page) and pulse
      // requestAnimationFrame to confirm the browser is still rendering actively — a frozen SDK
      // often blocks the main thread, which stops rAF too.
      // FaceTec runs in a cross-origin iframe, so its internal events don't bubble. We listen on
      // `window` for ANY global signal — postMessage from the iframe (FaceTec uses this heavily),
      // visibilitychange, focus/blur, or the user interacting with the host page. Any of these
      // resets the timer. A truly frozen SDK emits no postMessages and the user is locked out, so
      // after 120s with nothing we abort. Well past legitimate liveness (usually 15-45s).
      const WATCHDOG_MS = 45000;
      let openTimeoutId: ReturnType<typeof setTimeout> | undefined;
      const resetWatchdog = () => {
        if (callbackLockRef.current || !mountedRef.current) return;
        if (openTimeoutId) clearTimeout(openTimeoutId);
        openTimeoutId = setTimeout(() => {
          if (callbackLockRef.current || !mountedRef.current) return;
          callbackLockRef.current = true;
          logger.error('[Unico] sdk_watchdog_timeout', {
            process_id: getOrCreateProcessId(),
            window_ms: WATCHDOG_MS,
          });
          safeSetError('O SDK travou. Tente novamente.');
          safeSetStatus('error');
          releaseCameraResources();
          removeSdkErrorListeners();
          removeActivityListeners();
        }, WATCHDOG_MS);
      };
      const activityEvents: Array<keyof WindowEventMap> = [
        'pointerdown', 'pointermove', 'touchstart', 'click', 'keydown',
        'focus', 'blur', 'message',
      ];
      const attachActivityListeners = () => {
        activityEvents.forEach((evt) => window.addEventListener(evt, resetWatchdog, true));
        document.addEventListener('visibilitychange', resetWatchdog, true);
      };
      const removeActivityListeners = () => {
        activityEvents.forEach((evt) => window.removeEventListener(evt, resetWatchdog, true));
        document.removeEventListener('visibilitychange', resetWatchdog, true);
        if (openTimeoutId) clearTimeout(openTimeoutId);
      };
      attachActivityListeners();
      resetWatchdog();

      // Listen for unrecoverable SDK errors (faceworker/dorothy) that never surface as callbacks.
      // When detected, abort the session so the user can retry instead of staring at a frozen screen.
      const abortOnSdkError = (source: string, detail: unknown) => {
        if (callbackLockRef.current || !mountedRef.current) return;
        callbackLockRef.current = true;
        clearTimeout(openTimeoutId);
        logger.error('[Unico] sdk_internal_error_aborted', {
          process_id: getOrCreateProcessId(),
          source,
          detail: getUnicoErrorDetailsForLog(detail),
        });
        safeSetError('Falha interna do SDK. Tente novamente.');
        safeSetStatus('error');
        releaseCameraResources();
        removeSdkErrorListeners();
        removeActivityListeners();
      };

      const isSdkFailure = (text: string) =>
        text.includes('faceworker') ||
        text.includes('dorothy') ||
        text.includes('ozliveness') ||
        text.includes('FaceTec');

      const onWindowError = (event: ErrorEvent) => {
        const text = `${event.filename || ''} ${event.message || ''} ${String(event.error?.stack || '')}`;
        if (isSdkFailure(text)) abortOnSdkError('window.error', event.error || event.message);
      };
      const onUnhandled = (event: PromiseRejectionEvent) => {
        const text = `${String(event.reason?.stack || '')} ${String(event.reason?.message || '')}`;
        if (isSdkFailure(text)) abortOnSdkError('unhandledrejection', event.reason);
      };
      const removeSdkErrorListeners = () => {
        window.removeEventListener('error', onWindowError, true);
        window.removeEventListener('unhandledrejection', onUnhandled, true);
      };
      window.addEventListener('error', onWindowError, true);
      window.addEventListener('unhandledrejection', onUnhandled, true);


      cameraOpener.open({
          on: {
            success: (payload: { encrypted: string; base64: string }) => {
              if (callbackLockRef.current) {
                return;
              }
              callbackLockRef.current = true;
              clearTimeout(openTimeoutId);
              removeSdkErrorListeners();
              removeActivityListeners();

            safeSetStatus('processing');
            const issuedAt = new Date();
            const expiresAt = new Date(issuedAt.getTime() + TOKEN_TTL_SECONDS * 1000);
            const processId = getOrCreateProcessId();

            const captureResult: UnicoCaptureResult = {
              process_id: processId,
              provider: 'unico',
              capture_type: 'selfie_liveness',
              encrypted: payload.encrypted,
              raw_base64: payload.base64,
              token: {
                issued_at: issuedAt.toISOString(),
                expires_at: expiresAt.toISOString(),
                ttl_seconds: TOKEN_TTL_SECONDS,
                is_expired: false,
              },
              metadata: {
                captured_at: issuedAt.toISOString(),
                environment: configuredEnvironment,
                source: 'web',
                user_agent: navigator.userAgent,
                process_persistence: 'session_storage',
              },
              proxy: {
                status: 'pending',
                dispatched_at: null,
                payload: {
                  process_id: processId,
                  provider: 'unico',
                  capture_type: 'selfie_liveness',
                  encrypted: payload.encrypted,
                  metadata: {
                    captured_at: issuedAt.toISOString(),
                    environment: configuredEnvironment,
                    source: 'web',
                    user_agent: navigator.userAgent,
                    process_persistence: 'session_storage',
                  },
                  token: {
                    issued_at: issuedAt.toISOString(),
                    expires_at: expiresAt.toISOString(),
                    ttl_seconds: TOKEN_TTL_SECONDS,
                    is_expired: false,
                  },
                  integrations: {
                    serpro: {
                      score_result: null,
                      face_match_result: null,
                      base_consulted: null,
                      similarity_value: null,
                    },
                    salesforce: {
                      score_result: null,
                      face_match_result: null,
                      base_consulted: null,
                      similarity_value: null,
                    },
                  },
                },
              },
            };

            safeSetResult(captureResult);
            safeSetStatus('success');
            logger.info('[Unico] liveness_success', { process_id: processId });
            releaseCameraResources();

            const proxyPayload = buildProxyReadyPayload(captureResult);
            dispatchProxyPayload(proxyPayload)
              .then((proxyResult) => {
                if (proxyResult.status === 'skipped_expired') {
                  logger.warn('[Unico] token_expired', { process_id: processId });
                }
                safeSetResult({
                  ...captureResult,
                  token: { ...captureResult.token, is_expired: proxyResult.status === 'skipped_expired' },
                  proxy: {
                    ...captureResult.proxy,
                    status: proxyResult.status,
                    dispatched_at: proxyResult.dispatched_at,
                    payload: proxyPayload,
                  },
                });
              })
              .catch((err) => {
                logger.error('[Unico] proxy_dispatch_error', err);
                safeSetResult({
                  ...captureResult,
                  proxy: { ...captureResult.proxy, status: 'failed', dispatched_at: null },
                });
              });
          },
          error: (sdkError: unknown) => {
            if (callbackLockRef.current) {
              return;
            }
            callbackLockRef.current = true;
            clearTimeout(openTimeoutId);
            removeSdkErrorListeners();
            removeActivityListeners();

            const msg = mapErrorMessage(sdkError);

            // null = user cancelled — treat same as user_canceled callback
            if (msg === null) {
              logger.info('[Unico] user_canceled_via_error_callback', { process_id: getOrCreateProcessId() });
              safeSetError(null);
              safeSetStatus('idle');
              releaseCameraResources();
              return;
            }

            logger.error('[Unico] liveness_error', {
              process_id: getOrCreateProcessId(),
              error: getUnicoErrorDetailsForLog(sdkError),
            });
            safeSetError(msg);
            safeSetStatus('error');
            releaseCameraResources();
          },
          user_canceled: () => {
            if (callbackLockRef.current) {
              return;
            }
            callbackLockRef.current = true;
            clearTimeout(openTimeoutId);
            removeSdkErrorListeners();
            removeActivityListeners();
            safeSetError(null);
            safeSetStatus('idle');
            releaseCameraResources();
          },
        },
      });
    } catch (sdkError) {

      const errorDetails = getUnicoErrorDetailsForLog(sdkError);
      logger.error('[Unico] sdk_init_error', {
        process_id: getOrCreateProcessId(),
        error: errorDetails,
        stack: sdkError instanceof Error ? sdkError.stack : 'no-stack',
      });
      safeSetError(mapErrorMessage(sdkError));
      safeSetStatus('error');
      releaseCameraResources();
    }
  }, [
    // Removed: appEnvironment, configuredEnvironment, getOrCreateProcessId, hostByEnvironment, keyByEnvironment, modelsPath
    // These change frequently and cause Provider re-mount during SDK init, destroying state
    releaseCameraResources,
    sdkFailed,
    sdkReady,
    safeSetError,
    safeSetResult,
    safeSetStatus,
  ]);

  useEffect(() => {
    mountedRef.current = true;

    // Purge residual SDK state from previous tab sessions on mount (page reload / fresh nav).
    // Same treatment as clicking "Iniciar Captura" — ensures a reloaded tab behaves like an
    // anonymous one. Fire-and-forget: the user may take seconds to click, so by the time they do,
    // IndexedDB deletes have completed; openLivenessCapture still runs the async version for safety.
    performAggressiveUnicoCleanupAsync().catch(() => { /* non-fatal */ });

    return () => {
      mountedRef.current = false;
      callbackLockRef.current = true;
      releaseCameraResources();
    };
  }, [releaseCameraResources]);

  const value = useMemo<UnicoContextValue>(
    () => ({
      status: sdkReady ? status : 'sdk_loading',
      error,
      result,
      initializeSession,
      cleanupSession,
      openLivenessCapture,
      resetCapture,
    }),
    [cleanupSession, error, initializeSession, openLivenessCapture, resetCapture, result, sdkReady, status],
  );

  return <UnicoContext.Provider value={value}>{children}</UnicoContext.Provider>;
};

